import { View, ViewOpts } from '@play-co/timestep-core/ui';
import { SpineView } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import { PetEntity } from 'src/game/logic/EntityManager';
import { getPet } from 'src/game/pet';
import { createUIPet } from 'src/game/pet/pet.utils';
import { playSpineAnimation } from 'src/lib/ui/utils/spine';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { rescueLevelThemeDrowning } from '../themes/rescueLevel.theme.drowning';
import { waitForItPromise } from 'src/lib/utils';

export class HeaderDrowning extends View {
  private spineBg: SpineView;
  private spineFg: SpineView;
  public pet: PetEntity;
  private petBoneId = 'idle_placeholder';
  private failed = false;
  public tenSecsLeft = false;

  constructor(opts: ViewOpts) {
    super({
      ...opts,
      width: 720,
      height: 400,
    });

    this.spineBg = new SpineView({
      superview: this,
      spinePath: rescueLevelThemeDrowning.getHeaderBg(),
      scale: DOWNSCALE_FACTOR,
      x: this.style.width / 2,
      y: this.style.height / 2,
      zIndex: -1,
    });

    this.spineFg = new SpineView({
      superview: this,
      spinePath: rescueLevelThemeDrowning.getHeaderFg(),
      scale: DOWNSCALE_FACTOR,
      x: this.style.width / 2,
      y: this.style.height / 2,
      zIndex: 1,
    });

    // Listen to spine animation events
    this.spineFg.state.addListener({
      event: this.onSpineEvent.bind(this),
    });

    this.playIdle();

    this.pet = createUIPet('scared');
    this.pet.updateOpts({
      superview: this,
      x: this.style.width / 2,
      y: this.style.height / 2,
      anchorY: 100,
    });

    this.setTick(this.updatePetWithSpine.bind(this));
  }

  private onSpineEvent(track: unknown, event: { data: { name: string } }) {
    if (event.data.name !== 'switch') return;

    if (this.failed) {
      this.petBoneId = 'zombie_placeholder';
      // If failed, set to dead
      this.setPetAnimation('dead');
    } else if (this.petBoneId === 'idle_placeholder') {
      this.petBoneId = 'hanging_placeholder';
      // Pet is being rescued here, set to sad so it appears a bit apprehensive
      this.setPetAnimation('sad');
    } else if (this.petBoneId === 'hanging_placeholder') {
      this.petBoneId = 'idle_placeholder';
      // Pet has been rescued, set to happy
      this.setPetAnimation('happy');
    }
  }

  private updatePetWithSpine() {
    // Find the spine bone position to attach the pet to
    if (!this.spineFg) return;
    const bone = this.spineFg.skeleton.findBone(this.petBoneId);
    if (!bone) return;

    // Update the pet position to follow the bone
    this.pet.updateOpts({
      x: this.spineFg.style.x + bone.worldX,
      y: this.spineFg.style.y + bone.worldY,
      r: (bone.getWorldRotationX() * Math.PI) / 180,
    });
  }

  public async playIdle() {
    if (!this.spineBg || !this.spineFg) return;
    this.failed = false;
    this.tenSecsLeft = false;
    this.petBoneId = 'idle_placeholder';

    void playSpineAnimation(this.spineBg, {
      animation: 'idle',
      loop: true,
    });

    await playSpineAnimation(this.spineFg, {
      animation: 'idle',
      loop: true,
    });
  }

  public async playTenSecsLeft() {
    if (!this.spineBg || !this.spineFg) return;
    this.tenSecsLeft = true;

    void playSpineAnimation(this.spineBg, {
      animation: 'idle_to_tenseconds_left',
      loop: false,
    });

    await playSpineAnimation(this.spineFg, {
      animation: 'idle_to_tenseconds_left',
      loop: false,
    });

    void playSpineAnimation(this.spineBg, {
      animation: 'tenseconds_left',
      loop: true,
    });

    await playSpineAnimation(this.spineFg, {
      animation: 'tenseconds_left',
      loop: true,
    });
  }

  public async playRescue() {
    if (!this.spineBg || !this.spineFg) return;

    const animation = this.tenSecsLeft
      ? 'tenseconds_left_rescue'
      : 'idle_rescue';

    void playSpineAnimation(this.spineBg, {
      animation,
      loop: false,
    });

    await playSpineAnimation(this.spineFg, {
      animation,
      loop: false,
    });
  }

  public async playFail() {
    if (!this.spineBg || !this.spineFg) return;
    this.failed = true;

    const animation = 'tenseconds_left_to_lvl_fail2';
    const animationLoop = 'lvl_fail2';

    void playSpineAnimation(this.spineBg, {
      animation,
      loop: false,
    });

    await playSpineAnimation(this.spineFg, {
      animation,
      loop: false,
    });

    void playSpineAnimation(this.spineBg, {
      animation: animationLoop,
      loop: true,
    });

    void playSpineAnimation(this.spineFg, {
      animation: animationLoop,
      loop: true,
    });

    await waitForItPromise(2500);
  }

  public async playQuit() {
    if (!this.spineBg || !this.spineFg) return;
    this.setPetAnimation('dead');

    const animation = 'tenseconds_left_to_lvl_fail';

    void playSpineAnimation(this.spineBg, {
      animation,
      loop: false,
    });

    await playSpineAnimation(this.spineFg, {
      animation,
      loop: false,
    });
  }

  private setPetAnimation(status: 'happy' | 'dead' | 'sad') {
    const species = getPet().species ?? 'dog';
    this.pet.setAnimation(petAnimationNames[species][status].idle);
    if (status === 'happy') {
      this.pet.setStatusAnimation('happy');
    }
  }
}
